import { useQuery } from '@tanstack/react-query';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { AlertTriangle, Shield, Activity, Database, TrendingUp, Clock, CheckCircle, Loader2, Play } from 'lucide-react';

interface Stats {
  totalAddresses: number;
  blacklisted: number;
  pools: number;
  recentScans: number;
  riskDistribution: Array<{ risk_rating: string; count: number }>;
}

interface ScanStatus {
  id: string;
  status: 'running' | 'completed' | 'failed';
  progress: number;
  currentBlock: number;
  totalBlocks: number;
  addressesFound: number;
  poolsDetected: number;
  transactionsProcessed: number;
  startTime: number;
  message: string;
}

const RISK_COLORS = {
  safe: '#10b981',
  low: '#3b82f6',
  medium: '#f59e0b',
  high: '#ef4444',
  critical: '#991b1b',
};

export function Dashboard() {
  const { data: stats, isLoading, error } = useQuery<Stats>({
    queryKey: ['stats'],
    queryFn: async () => {
      // Return default empty stats for now
      // In production, this would query the D1 database via API
      return {
        totalAddresses: 0,
        blacklisted: 0,
        pools: 0,
        recentScans: 0,
        riskDistribution: [
          { risk_rating: 'safe', count: 0 },
          { risk_rating: 'low', count: 0 },
          { risk_rating: 'medium', count: 0 },
          { risk_rating: 'high', count: 0 },
          { risk_rating: 'critical', count: 0 },
        ],
      };
    },
    refetchInterval: 5000, // Refresh every 5 seconds
  });

  // Poll for active scans - check API for any running scans
  const { data: activeScan } = useQuery<ScanStatus | null>({
    queryKey: ['activeScan'],
    queryFn: async () => {
      // In a full implementation, we'd have an endpoint to list all scans
      // For now, return null (no active scan)
      return null;
    },
    refetchInterval: 2000, // Check every 2 seconds
  });

  if (isLoading) {
    return (
      <div className="flex flex-col justify-center items-center h-screen">
        <Loader2 size={48} className="spin text-blue-500 mb-4" />
        <p className="text-gray-400 text-lg">
          Loading dashboard data...
        </p>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Total Addresses',
      value: stats?.totalAddresses || 0,
      icon: Database,
      color: 'var(--color-primary)',
      trend: '+12%',
    },
    {
      title: 'Blacklisted',
      value: stats?.blacklisted || 0,
      icon: AlertTriangle,
      color: 'var(--color-danger)',
      trend: '+5%',
    },
    {
      title: 'Identified Pools',
      value: stats?.pools || 0,
      icon: Shield,
      color: 'var(--color-warning)',
      trend: '+3',
    },
    {
      title: 'Recent Scans (7d)',
      value: stats?.recentScans || 0,
      icon: Activity,
      color: 'var(--color-success)',
      trend: 'Active',
    },
  ];

  const chartData = stats?.riskDistribution.map((item) => ({
    name: item.risk_rating.charAt(0).toUpperCase() + item.risk_rating.slice(1),
    count: item.count,
    color: RISK_COLORS[item.risk_rating as keyof typeof RISK_COLORS] || '#6b7280',
  })) || [];

  const pieData = chartData.map(item => ({
    name: item.name,
    value: item.count,
    color: item.color,
  }));

  const formatTime = (timestamp: number) => {
    const seconds = Math.floor((Date.now() - timestamp) / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ${seconds % 60}s`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ${minutes % 60}m`;
  };

  return (
    <div className="animate-fadeIn">
      {/* Header */}
      <div className="flex flex-wrap justify-between items-center mb-8 gap-4">
        <div>
          <h1 className="text-4xl font-bold mb-2 bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
            Token Abuse Monitor
          </h1>
          <p className="text-gray-400 text-lg">
            Real-time monitoring of VERSE reward farming abuse
          </p>
        </div>
        <div className="flex gap-3">
          <a 
            href="/scanner" 
            className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-105"
          >
            <Play size={18} />
            New Scan
          </a>
          <a 
            href="/lookup" 
            className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white font-semibold rounded-lg transition-all duration-200"
          >
            Lookup Address
          </a>
        </div>
      </div>

      {/* Active Scan Status */}
      {activeScan && activeScan.status === 'running' && (
        <div className="mb-8 p-6 rounded-xl bg-gradient-to-br from-blue-500/10 to-green-500/10 border-2 border-blue-500/50 backdrop-blur-sm">
          <div className="flex items-start gap-6">
            <div className="p-4 rounded-full bg-blue-600 animate-pulse">
              <Loader2 size={24} className="spin text-white" />
            </div>
            <div className="flex-1">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-xl font-semibold text-white">
                  Scan in Progress
                </h3>
                <span className="px-4 py-1 rounded-full bg-green-600 text-white text-sm font-medium">
                  {activeScan.progress}%
                </span>
              </div>
              <p className="text-gray-300 mb-4">
                {activeScan.message}
              </p>
              
              {/* Progress Bar */}
              <div className="w-full h-2 bg-gray-700 rounded-full overflow-hidden mb-4">
                <div 
                  className="h-full bg-gradient-to-r from-blue-500 to-green-500 transition-all duration-300 ease-out rounded-full"
                  style={{ width: `${activeScan.progress}%` }}
                />
              </div>

              {/* Scan Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs text-gray-400 mb-1">
                    Blocks Scanned
                  </p>
                  <p className="text-lg font-semibold text-white">
                    {activeScan.currentBlock.toLocaleString()} / {activeScan.totalBlocks.toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-1">
                    Addresses Found
                  </p>
                  <p className="text-lg font-semibold text-blue-400">
                    {activeScan.addressesFound.toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-1">
                    Pools Detected
                  </p>
                  <p className="text-lg font-semibold text-yellow-400">
                    {activeScan.poolsDetected}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-1">
                    Running Time
                  </p>
                  <p className="text-lg font-semibold text-white">
                    {formatTime(activeScan.startTime)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((card, index) => {
          const Icon = card.icon;
          const iconColorClass = card.title === 'Total Addresses' ? 'text-blue-500' :
                                 card.title === 'Blacklisted' ? 'text-red-500' :
                                 card.title === 'Identified Pools' ? 'text-yellow-500' :
                                 'text-green-500';
          const bgColorClass = card.title === 'Total Addresses' ? 'bg-blue-500/10' :
                               card.title === 'Blacklisted' ? 'bg-red-500/10' :
                               card.title === 'Identified Pools' ? 'bg-yellow-500/10' :
                               'bg-green-500/10';
          
          return (
            <div 
              key={card.title} 
              className="p-6 rounded-xl bg-gray-800/50 border border-gray-700 backdrop-blur-sm hover:-translate-y-1 hover:shadow-2xl hover:border-gray-600 transition-all duration-200 cursor-pointer"
              style={{ animation: `slideUp 0.4s ease-out ${index * 0.1}s backwards` }}
            >
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <p className="text-gray-400 text-sm font-medium mb-2">
                    {card.title}
                  </p>
                  <p className="text-4xl font-bold text-white">
                    {card.value.toLocaleString()}
                  </p>
                </div>
                <div className={`p-3 rounded-lg ${bgColorClass}`}>
                  <Icon size={28} className={iconColorClass} />
                </div>
              </div>
              <div className="flex items-center gap-2 text-green-400 text-sm font-medium">
                <TrendingUp size={16} />
                <span>{card.trend}</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Bar Chart */}
        <div className="p-6 rounded-xl bg-gray-800/50 border border-gray-700 backdrop-blur-sm">
          <h2 className="text-xl font-bold text-white mb-6">
            Risk Distribution
          </h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
              <XAxis 
                dataKey="name" 
                stroke="#9CA3AF"
                style={{ fontSize: '0.875rem' }}
              />
              <YAxis 
                stroke="#9CA3AF"
                style={{ fontSize: '0.875rem' }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1F2937',
                  border: '1px solid #374151',
                  borderRadius: '0.5rem',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                }}
              />
              <Bar dataKey="count" radius={[8, 8, 0, 0]}>
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Pie Chart */}
        <div className="p-6 rounded-xl bg-gray-800/50 border border-gray-700 backdrop-blur-sm">
          <h2 className="text-xl font-bold text-white mb-6">
            Risk Breakdown
          </h2>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1F2937',
                  border: '1px solid #374151',
                  borderRadius: '0.5rem',
                }}
              />
              <Legend 
                verticalAlign="bottom" 
                height={36}
                iconType="circle"
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* System Status */}
      <div className="p-6 rounded-xl bg-gray-800/50 border border-gray-700 backdrop-blur-sm">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-white">
            System Status
          </h2>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            <span className="text-sm text-green-400 font-medium">
              All Systems Operational
            </span>
          </div>
        </div>
        
        <div className="space-y-3">
          <div className="flex justify-between items-center p-4 bg-gray-700/30 rounded-lg">
            <div className="flex items-center gap-3">
              <CheckCircle size={20} className="text-green-500" />
              <span className="text-gray-200">Blockchain RPC Connection</span>
            </div>
            <span className="text-green-400 font-medium">Connected</span>
          </div>
          
          <div className="flex justify-between items-center p-4 bg-gray-700/30 rounded-lg">
            <div className="flex items-center gap-3">
              <CheckCircle size={20} className="text-green-500" />
              <span className="text-gray-200">Database Status</span>
            </div>
            <span className="text-green-400 font-medium">Healthy</span>
          </div>
          
          <div className="flex justify-between items-center p-4 bg-gray-700/30 rounded-lg">
            <div className="flex items-center gap-3">
              <Clock size={20} className="text-blue-500" />
              <span className="text-gray-200">Last Update</span>
            </div>
            <span className="text-gray-400">Just now</span>
          </div>
        </div>
      </div>
    </div>
  );
}
export default Dashboard;
